/**
 * The Live Agent Platforms page's notion of "loaded".
 *
 * What this file pins: the catalog counts as loaded once `pypeConnections`,
 * `liveAgentPypes` and `agentRoutingSchemas` are all present (`catalogLoaded`),
 * and `readyToRender` is returned as that SAME value, so a missing catalog
 * field keeps both false.
 *
 * What it does NOT guard: `readyToRender` equals `catalogLoaded` by
 * construction, so a test comparing the two checks one value against itself.
 * The function takes no argument for `ctx.solutions`, which is what keeps the
 * table from waiting for them; a caller could still add such a wait in the
 * page. The condition under which the page shows the table lives in the page
 * component, which imports the design system and has no unit test.
 *
 * Why the table does not wait for `ctx.solutions`: they are loaded by the
 * `loadingBots` state (`loadBots`), which is not in `NON_LOADING_STUDIO_STATES`
 * (hooks/loading-states.ts), so while it runs `WithLoadingState` hides this
 * page anyway. It can leave WITHOUT them: on an error it goes to `#error` (a
 * non-loading state, so the page shows) with `ctx.solutions` untouched,
 * `undefined` if they were never loaded, or after a solution delete reset
 * them. Waiting for them would then keep the section loader up for good.
 * Until they are known each row's channels are one flat list
 * (`groupChannelsBySolution` with no resolver), never a false "No solution"
 * box; `components/solution-channel-groups/use-channel-groups.test.tsx` pins
 * that flat-list behaviour.
 *
 * - `catalogLoaded`: what `loadLiveAgentPlatforms` provides. It gates the
 *   page's own (re)dispatch of that event and the Add button.
 * - `readyToRender`: the table may be drawn; same condition as `catalogLoaded`.
 */
export const resolveLiveAgentPlatformsLoadState = (data: {
  pypeConnections: unknown;
  liveAgentPypes: unknown;
  agentRoutingSchemas: unknown;
}): { catalogLoaded: boolean; readyToRender: boolean } => {
  const catalogLoaded =
    data.pypeConnections != null &&
    data.liveAgentPypes != null &&
    data.agentRoutingSchemas != null;
  return { catalogLoaded, readyToRender: catalogLoaded };
};
